import { 
    collection, query, orderBy, getDocs, doc, addDoc, updateDoc, 
    deleteDoc, writeBatch, serverTimestamp, getDoc 
} from 'firebase/firestore';
import { 
    getStorage, ref, uploadBytes, getDownloadURL, deleteObject 
} from 'firebase/storage';
import { db } from '../config/firebase';

const PRODUCTS_COL = 'products';
const PARAMS_COL = 'parameters';

export const productService = {
    /**
     * Obtiene todos los productos y la configuración de unidades.
     */
    getInitialData: async () => {
        try {
            const [productsSnap, paramsSnap] = await Promise.all([
                getDocs(query(collection(db, PRODUCTS_COL), orderBy('name'))),
                getDoc(doc(db, PARAMS_COL, 'system-settings'))
            ]);

            const products = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Fallback de parámetros si no existe
            const parameters = paramsSnap.exists() ? paramsSnap.data() : { unidades: ['unid', 'm', 'kg', 'ltr', 'caja'] };

            return { products, parameters };
        } catch (error) {
            console.error("Error obteniendo productos:", error);
            throw error;
        }
    },

    /**
     * Sube un archivo a Storage (Imagen o PDF).
     */
    uploadFile: async (file, type) => {
        if (!file) return null;
        try {
            const storage = getStorage();
            // products/TIMESTAMP_TYPE_FILENAME
            const refName = `products/${Date.now()}_${type}_${file.name}`;
            const storageRef = ref(storage, refName);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error(`Error subiendo ${type}:`, error);
            throw error;
        }
    },

    /**
     * Guarda un producto (Crear o Editar).
     * Maneja la subida de archivos si se proporcionan.
     */
    saveProduct: async ({ productData, imageFile, pdfFile, isEditMode }) => {
        try {
            let finalImageUrl = productData.imageUrl;
            let finalPdfUrl = productData.pdfUrl;

            // 1. Subir archivos nuevos si existen
            if (imageFile) {
                finalImageUrl = await productService.uploadFile(imageFile, 'img');
            }
            if (pdfFile) {
                finalPdfUrl = await productService.uploadFile(pdfFile, 'doc');
            }

            // 2. Preparar objeto para Firestore
            const dataToSave = {
                ...productData,
                imageUrl: finalImageUrl || '',
                pdfUrl: finalPdfUrl || '',
                updatedAt: serverTimestamp()
            };

            // --- CORRECCIÓN CRÍTICA ---
            // Firestore lanza error si 'id' es undefined. Lo eliminamos del objeto a guardar.
            delete dataToSave.id;
            // --------------------------

            if (!isEditMode) {
                dataToSave.createdAt = serverTimestamp();
            }

            // 3. Guardar en Firestore
            if (isEditMode && productData.id) {
                await updateDoc(doc(db, PRODUCTS_COL, productData.id), dataToSave);
                return { id: productData.id, ...dataToSave };
            } else {
                const docRef = await addDoc(collection(db, PRODUCTS_COL), dataToSave);
                return { id: docRef.id, ...dataToSave };
            }

        } catch (error) {
            console.error("Error guardando producto:", error);
            throw error;
        }
    },

    /**
     * Elimina un producto y sus archivos asociados.
     */
    deleteProduct: async (product) => {
        try {
            const storage = getStorage();
            
            // Intentar borrar imagen
            if (product.imageUrl && product.imageUrl.includes('firebase')) {
                try {
                    const imgRef = ref(storage, product.imageUrl);
                    await deleteObject(imgRef);
                } catch (e) { console.warn("Imagen ya borrada o no accesible"); }
            }

            // Intentar borrar PDF
            if (product.pdfUrl && product.pdfUrl.includes('firebase')) {
                try {
                    const pdfRef = ref(storage, product.pdfUrl);
                    await deleteObject(pdfRef);
                } catch (e) { console.warn("PDF ya borrado o no accesible"); }
            }

            // Borrar documento
            await deleteDoc(doc(db, PRODUCTS_COL, product.id));
            
        } catch (error) {
            console.error("Error eliminando producto:", error);
            throw error;
        }
    },

    /**
     * Importación masiva desde CSV (Batch Write).
     */
    importFromCSV: async (csvText, defaultType = 'simple') => {
        try {
            const lines = csvText.split('\n');
            const batch = writeBatch(db);
            let count = 0;

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                const cols = line.split(';');
                if (cols.length < 4) continue; // Mínimo nombre, tipo, unidad, costo

                const typeFromCsv = (cols[1] || '').trim().toLowerCase();
                const finalType = typeFromCsv || defaultType;

                const productData = {
                    name: cols[0].trim(),
                    type: finalType,
                    unit: cols[2].trim() || 'unid',
                    cost: parseFloat(cols[3]) || 0,
                    description: cols[4]?.trim() || '',
                    imageUrl: cols[5]?.trim() || '',
                    pdfUrl: cols[6]?.trim() || '',
                    stock: 0, // Stock inicial 0 por seguridad
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    ...(finalType === 'kit' ? { components: [] } : {})
                };

                const newRef = doc(collection(db, PRODUCTS_COL));
                batch.set(newRef, productData);
                count++;
            }

            if (count > 0) {
                await batch.commit();
            }
            return count;

        } catch (error) {
            console.error("Error importando CSV:", error);
            throw error;
        }
    }
};